import { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function Reports() {
  const { selectedBusiness } = useAuth();
  const bid = selectedBusiness?.id;
  const [stats, setStats] = useState({ total_sent: 0, total_delivered: 0, total_read: 0, total_failed: 0, total_pending: 0 });
  const [allFailedModal, setAllFailedModal] = useState(false);
  const [allFailedList, setAllFailedList] = useState([]);
  const [allFailedLoading, setAllFailedLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [failedModal, setFailedModal] = useState(null);
  const [failedContacts, setFailedContacts] = useState([]);
  const [failedLoading, setFailedLoading] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [viewMessages, setViewMessages] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [datePreset, setDatePreset] = useState('all');
  const [utmData, setUtmData] = useState(null);
  const [utmLoading, setUtmLoading] = useState(true);
  const [utmSearch, setUtmSearch] = useState('');
  const [templateFilter, setTemplateFilter] = useState('all');

  const applyPreset = (preset) => {
    setDatePreset(preset);
    const now = new Date();
    const fmt = d => d.toISOString().slice(0,10);
    if (preset === 'today') { setDateFrom(fmt(now)); setDateTo(fmt(now)); }
    else if (preset === 'week') { const d = new Date(now); d.setDate(d.getDate()-6); setDateFrom(fmt(d)); setDateTo(fmt(now)); }
    else if (preset === 'month') { const d = new Date(now); d.setDate(1); setDateFrom(fmt(d)); setDateTo(fmt(now)); }
    else { setDateFrom(''); setDateTo(''); }
  };

  const fetchData = () => {
    if (!bid) return;
    api.get(`/dashboard/stats?business_id=${bid}`).then(r => setStats(r.data)).catch(() => {});
    api.get(`/campaigns?business_id=${bid}`).then(r => setCampaigns(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };

  useEffect(() => {
    if (!bid) return;
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [bid]);

  const fetchOrderSource = () => {
    if (!bid) return;
    setUtmLoading(true);
    let url = `/dashboard/order-source?business_id=${bid}`;
    if (dateFrom) url += `&from=${dateFrom}`;
    if (dateTo) url += `&to=${dateTo}`;
    api.get(url).then(r => setUtmData(r.data)).catch(() => setUtmData(null)).finally(() => setUtmLoading(false));
  };

  useEffect(() => {
    fetchOrderSource();
  }, [bid, dateFrom, dateTo]);

  const parseName = (name, apiCustomerName, apiCustomerPhone) => {
    if (!name) return { orderId: '-', customerName: apiCustomerPhone || apiCustomerName || '-', orderName: '-' };
    // GSM Trans India: show the real customer phone (from contacts, via
    // apiCustomerPhone) instead of parsing it out of campaign_name, since
    // API-triggered campaigns (e.g. "api_location_...") don't carry the
    // phone number in that string at all.
    if (bid === 22) {
      return {
        orderId: '-',
        customerName: apiCustomerName || '-',
        orderName: apiCustomerPhone || '-',
      };
    }
    const match = name.match(/^(.+?)_(\d+)_(.+)$/);
    if (match) {
      return {
        orderName: match[1] + '_' + match[2],
        orderId: match[2],
        customerName: apiCustomerName || match[3],
      };
    }
    const match2 = name.match(/^(.+?)_(\d+)$/);
    if (match2) {
      return {
        orderName: name,
        orderId: match2[2],
        customerName: apiCustomerName || '-',
      };
    }
    return { orderId: '-', customerName: apiCustomerName || name, orderName: name };
  };

  const downloadCSV = (data, filename, headers, rowFn) => {
    const csvRows = [headers.join(',')];
    data.forEach(item => csvRows.push(rowFn(item).map(v => {
      const s = v ?? '';
      return typeof s === 'string' && s.startsWith('="') ? s : `"${s}"`;
    }).join(',')));
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCampaignCSV = (c) => {
    const { orderId, customerName, orderName } = parseName(c.campaign_name, c.customer_name, c.customer_phone);
    const headers = bid === 22
      ? ['Customer Name', 'Phone Number', 'Template', 'Total', 'Sent', 'Delivered', 'Delivered%', 'Read', 'Read%', 'Failed', 'Date']
      : ['Order ID', 'Customer Name', 'Order Name', 'Template', 'Total', 'Sent', 'Delivered', 'Delivered%', 'Read', 'Read%', 'Failed', 'Date'];
    downloadCSV([c], `${c.campaign_name}_${new Date().toISOString().slice(0,10)}.csv`,
      headers,
      c => {
        const total = c.sent_count ?? c.total_contacts ?? 0;
        const safeOrderName = bid === 22 && orderName && orderName !== '-' ? `="${orderName}"` : orderName;
        const row = [customerName, safeOrderName, c.template_name, total, c.sent_count,
          c.delivered_count, total > 0 ? Math.round((c.delivered_count/total)*100)+'%' : '0%',
          c.read_count ?? 0, c.delivered_count > 0 ? Math.round(((c.read_count??0)/c.delivered_count)*100)+'%' : '0%',
          c.failed_count, c.created_at ? new Date(c.created_at).toLocaleDateString('en-GB') : ''];
        return bid === 22 ? row : [orderId, ...row];
      }
    );
  };

  const downloadAllCSV = (templateOnly) => {
    const dataToExport = templateOnly ? campaigns.filter(c => c.template_name === templateOnly) : campaigns;
    const fileName = templateOnly ? `${templateOnly}_${new Date().toISOString().slice(0,10)}.csv` : `all_campaigns_${new Date().toISOString().slice(0,10)}.csv`;
    const headers = bid === 22
      ? ['Customer Name', 'Phone Number', 'Template', 'Total', 'Sent', 'Delivered', 'Delivered%', 'Read', 'Read%', 'Failed', 'Date']
      : ['Order ID', 'Customer Name', 'Order Name', 'Template', 'Total', 'Sent', 'Delivered', 'Delivered%', 'Read', 'Read%', 'Failed', 'Date'];
    downloadCSV(dataToExport, fileName,
      headers,
      c => {
        const { orderId, customerName, orderName } = parseName(c.campaign_name, c.customer_name, c.customer_phone);
        const total = c.sent_count ?? c.total_contacts ?? 0;
        const safeOrderName = bid === 22 && orderName && orderName !== '-' ? `="${orderName}"` : orderName;
        const row = [customerName, safeOrderName, c.template_name, total, c.sent_count,
          c.delivered_count, total > 0 ? Math.round((c.delivered_count/total)*100)+'%' : '0%',
          c.read_count ?? 0, c.delivered_count > 0 ? Math.round(((c.read_count??0)/c.delivered_count)*100)+'%' : '0%',
          c.failed_count, c.created_at ? new Date(c.created_at).toLocaleDateString('en-GB') : ''];
        return bid === 22 ? row : [orderId, ...row];
      }
    );
  };

  const fetchFailed = async (campaign) => {
    setFailedModal(campaign);
    setFailedLoading(true);
    setFailedContacts([]);
    try {
      const res = await api.get(`/campaigns/${campaign.id}/failed`);
      setFailedContacts(Array.isArray(res.data) ? res.data : []);
    } catch(e) { setFailedContacts([]); }
    setFailedLoading(false);
  };

  const fetchView = async (campaign) => {
    setViewModal(campaign);
    setViewLoading(true);
    setViewMessages([]);
    try {
      const res = await api.get(`/messages?business_id=${bid}&template_name=${campaign.template_name}&limit=5000`);
      const data = Array.isArray(res.data) ? res.data : (res.data?.messages || []);
      setViewMessages(data);
    } catch(e) { setViewMessages([]); }
    setViewLoading(false);
  };

  const downloadViewCSV = (type) => {
    const filtered = type === 'success'
      ? viewMessages.filter(m => m.status === 'delivered' || m.status === 'read')
      : type === 'failed'
      ? viewMessages.filter(m => m.status === 'failed')
      : viewMessages;
    const suffix = type === 'success' ? '_success' : type === 'failed' ? '_failed' : '';
    const reasonOf = m => {
      if (m.status === 'read') return 'Read by customer';
      if (m.status === 'delivered') return 'Delivered to device';
      if (m.status === 'failed') return (m.error_code ? `[${m.error_code}] ` : '') + (m.error_reason || 'Unknown error');
      return m.status || '';
    };
    const errorMeanings = {
      '131049': 'Customer not receptive right now (Meta ecosystem protection). Wait 24 hours, do not resend immediately.',
      '131026': 'Number not on WhatsApp, or has not accepted latest Terms of Service, or using an old WhatsApp version.',
      '130472': 'Meta has this number in a marketing message experiment group. No action needed on your side.',
      '131047': 'More than 24 hours passed since customer last replied. Use a template message instead.',
      '131048': 'Too many previous messages were blocked/flagged as spam. Check phone number quality rating.',
      '131050': 'Customer has opted out of marketing messages from your business. Do not retry.',
      '131051': 'Unsupported message type for this template.',
      '130403': 'This business has blocked the customer on WhatsApp. Unblock to resume sending.'
    };
    const meaningOf = m => m.status === 'failed' && m.error_code ? (errorMeanings[String(m.error_code)] || 'See Meta error code documentation for details.') : '';
    downloadCSV(filtered, `${viewModal?.campaign_name}${suffix}_messages.csv`,
      ['Name', 'Phone', 'Template', 'Status', 'Reason', 'What It Means', 'Sent At', 'Delivered At', 'Read At'],
      m => [m.name || '-', `="${m.phone}"`, m.template_name || '', m.status, reasonOf(m), meaningOf(m),
        m.created_at ? new Date(m.created_at).toLocaleString() : '',
        m.delivered_at ? new Date(m.delivered_at).toLocaleString() : '',
        m.read_at ? new Date(m.read_at).toLocaleString() : '']
    );
  };

  const pct = (val, total) => total > 0 ? Math.min(Math.round((val / total) * 100), 100) : 0;

  const fetchAllFailed = async () => {
    setAllFailedModal(true);
    setAllFailedLoading(true);
    setAllFailedList([]);
    try {
      const res = await api.get(`/messages/failed?business_id=${bid}`);
      setAllFailedList(Array.isArray(res.data) ? res.data : []);
    } catch(e) { setAllFailedList([]); }
    setAllFailedLoading(false);
  };

  const statusStyle = (s) => ({
    sent:      { bg: '#ecfdf5', color: '#059669', dot: '#10b981' },
    delivered: { bg: '#eff6ff', color: '#2563eb', dot: '#3b82f6' },
    read:      { bg: '#f5f3ff', color: '#7c3aed', dot: '#8b5cf6' },
    failed:    { bg: '#fef2f2', color: '#dc2626', dot: '#ef4444' },
    received:  { bg: '#fefce8', color: '#b45309', dot: '#f59e0b' },
  }[s] || { bg: '#f9fafb', color: '#6b7280', dot: '#9ca3af' });

  const totalSent = parseInt(stats.total_sent) || 0;
  const totalDelivered = parseInt(stats.total_delivered) || 0;
  const totalRead = parseInt(stats.total_read) || 0;
  const totalFailed = parseInt(stats.total_failed) || 0;
  const totalUndelivered = parseInt(stats.total_pending) || 0;

  const filteredCampaigns = campaigns.filter(c => {
    const { orderId, customerName, orderName } = parseName(c.campaign_name, c.customer_name, c.customer_phone);
    if (search) {
      const s = search.toLowerCase();
      if (!orderId.includes(s) && !customerName.toLowerCase().includes(s) && !orderName.toLowerCase().includes(s)) return false;
    }
    if (dateFrom || dateTo) {
      const d = c.created_at ? new Date(c.created_at) : null;
      if (!d) return false;
      const dStr = d.toISOString().slice(0,10);
      if (dateFrom && dStr < dateFrom) return false;
      if (dateTo && dStr > dateTo) return false;
    }
    if (bid === 26 && templateFilter !== 'all' && c.template_name !== templateFilter) return false;
    return true;
  });
  const templateNames = bid === 26 ? [...new Set(campaigns.map(c => c.template_name).filter(Boolean))] : [];

  const graphData = (() => {
    const map = {};
    filteredCampaigns.forEach(c => {
      const d = c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short' }) : 'Unknown';
      if (!map[d]) map[d] = { sent: 0, delivered: 0, read: 0 };
      map[d].sent += c.sent_count || 0;
      map[d].delivered += c.delivered_count || 0;
      map[d].read += c.read_count || 0;
    });
    return Object.entries(map).slice(-10).map(([date, v]) => ({ date, ...v }));
  })();
  const maxVal = Math.max(...graphData.map(d => d.sent), 1);

  if (!bid) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontFamily: 'DM Sans, sans-serif' }}>
      Please select a business first.
    </div>
  );

  return (
    <div style={{ padding: '28px 32px', fontFamily: 'DM Sans, sans-serif', background: '#f8fafc', minHeight: '100vh', fontSize: '15px' }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>Analytics & Reports</div>
        <div style={{ fontSize: 14, color: '#64748b', marginTop: 4 }}>Track your WhatsApp campaign performance</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
        {[
          { label: 'Total Sent', value: totalSent, icon: '📤', color: '#3b82f6', bg: '#eff6ff', sub: null },
          { label: 'Delivered', value: totalDelivered, icon: '✅', color: '#10b981', bg: '#ecfdf5', sub: `${totalRead} read` },
          { label: 'Undelivered', value: totalUndelivered, icon: '⏳', color: '#f59e0b', bg: '#fffbeb', sub: 'sent, not delivered' },
          { label: 'Failed', value: totalFailed, icon: '❌', color: '#ef4444', bg: '#fef2f2', sub: null, onClick: fetchAllFailed },
        ].map((s, i) => (
          <div key={i} onClick={s.onClick} style={{ background: '#fff', borderRadius: 14, padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', cursor: s.onClick ? 'pointer' : 'default' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>{s.label}</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: s.color }}>{s.value.toLocaleString()}</div>
                {s.sub && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{s.sub}</div>}
              </div>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{s.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {graphData.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '20px 24px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>📊 Message Activity (Last 10 Days)</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 120 }}>
            {graphData.map((d, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 2, height: 90 }}>
                  <div title={`Sent: ${d.sent}`} style={{ width: '100%', height: `${(d.sent/maxVal)*90}px`, background: '#3b82f6', borderRadius: '4px 4px 0 0', minHeight: d.sent>0?4:0 }} />
                </div>
                <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', whiteSpace: 'nowrap' }}>{d.date}</div>
                <div style={{ fontSize: 10, color: '#3b82f6', fontWeight: 700 }}>{d.sent}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
            <span style={{ fontSize: 12, color: '#3b82f6' }}>🔵 Sent</span>
          </div>
        </div>
      )}
      {!utmLoading && utmData && utmData.summary && utmData.summary.length > 0 && (() => {
        const sourceColors = { Instagram: '#e1306c', Facebook: '#1877f2', Google: '#ea4335', WhatsApp: '#25D366', YouTube: '#ff0000', Direct: '#64748b', Other: '#94a3b8' };
        const topSource = utmData.summary[0];
        const pieData = utmData.summary.map(s => ({ name: s.source, value: s.orders }));
        const filteredOrders = (utmData.orders || []).filter(o => {
          if (!utmSearch) return true;
          const s = utmSearch.toLowerCase();
          return (o.order_name||'').toLowerCase().includes(s) || (o.customer||'').toLowerCase().includes(s) || (o.utm_source||'').toLowerCase().includes(s);
        });
        return (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 14 }}>🛍️ Order Source (UTM) Report</div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
              {[
                { label: 'Total Orders', value: utmData.totalOrders, icon: '📦', color: '#3b82f6', bg: '#eff6ff' },
                { label: 'Total Revenue', value: `₹${Math.round(utmData.totalRevenue).toLocaleString()}`, icon: '💰', color: '#16a34a', bg: '#f0fdf4' },
                { label: 'Sources', value: utmData.summary.length, icon: '🔀', color: '#7c3aed', bg: '#f5f3ff' },
                { label: 'Top Source', value: topSource.source, icon: '🏆', color: sourceColors[topSource.source] || '#f59e0b', bg: '#fffbeb' },
              ].map((s, i) => (
                <div key={i} style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>{s.label}</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                    </div>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17 }}>{s.icon}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>Orders by Source</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Source','Orders','Revenue','Share'].map(h => (
                        <th key={h} style={{ padding: '8px 6px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: h==='Source'?'left':'right', borderBottom: '1px solid #f1f5f9' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {utmData.summary.map((s, i) => {
                      const share = utmData.totalOrders > 0 ? Math.round((s.orders / utmData.totalOrders) * 100) : 0;
                      const color = sourceColors[s.source] || '#3b82f6';
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #f8fafc' }}>
                          <td style={{ padding: '10px 6px', fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                            <span style={{ display:'inline-block', width:8, height:8, borderRadius:'50%', background: color, marginRight: 8 }} />
                            {s.source}
                          </td>
                          <td style={{ padding: '10px 6px', fontSize: 13, color: '#374151', textAlign: 'right', fontWeight: 600 }}>{s.orders}</td>
                          <td style={{ padding: '10px 6px', fontSize: 13, color: '#16a34a', textAlign: 'right', fontWeight: 600 }}>₹{Math.round(s.revenue).toLocaleString()}</td>
                          <td style={{ padding: '10px 6px', fontSize: 13, color: '#64748b', textAlign: 'right' }}>{share}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Source Distribution</div>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2}>
                      {pieData.map((entry, i) => (
                        <Cell key={i} fill={sourceColors[entry.name] || '#3b82f6'} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value} orders`, name]} />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Order Details</div>
                <input
                  placeholder="Search order / customer / source..."
                  value={utmSearch}
                  onChange={e => setUtmSearch(e.target.value)}
                  style={{ padding: '7px 14px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none', minWidth: 220 }}
                />
              </div>
              <div style={{ overflowX: 'auto', maxHeight: 360, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {['Order ID','Customer','Total','Status','UTM Source','UTM Medium','UTM Campaign'].map(h => (
                        <th key={h} style={{ padding: '10px 16px', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #e2e8f0', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.length === 0 ? (
                      <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>No orders found</td></tr>
                    ) : filteredOrders.map((o, i) => {
                      const color = sourceColors[o.utm_source] || '#3b82f6';
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '11px 16px', fontWeight: 700, color: '#2563eb', fontSize: 13 }}>{o.order_name}</td>
                          <td style={{ padding: '11px 16px', fontWeight: 600, color: '#0f172a', fontSize: 13 }}>{o.customer}</td>
                          <td style={{ padding: '11px 16px', fontSize: 13, color: '#16a34a', fontWeight: 600 }}>₹{Math.round(parseFloat(o.total||0)).toLocaleString()}</td>
                          <td style={{ padding: '11px 16px' }}>
                            <span style={{ padding: '3px 10px', borderRadius: 20, background: '#f1f5f9', color: '#475569', fontSize: 11, fontWeight: 700, textTransform: 'capitalize' }}>{o.status}</span>
                          </td>
                          <td style={{ padding: '11px 16px', fontSize: 13 }}>
                            <span style={{ display:'inline-flex', alignItems:'center', gap:6, padding: '3px 10px', borderRadius: 20, background: '#f8fafc', color: color, fontSize: 12, fontWeight: 700 }}>
                              <span style={{ width:6, height:6, borderRadius:'50%', background: color }} />
                              {o.utm_source}
                            </span>
                          </td>
                          <td style={{ padding: '11px 16px', fontSize: 13, color: '#64748b' }}>{o.utm_medium}</td>
                          <td style={{ padding: '11px 16px', fontSize: 13, color: '#64748b' }}>{o.utm_campaign}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Campaign Reports</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{campaigns.length} campaigns total</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              placeholder="Search order id / name..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: '8px 14px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none', minWidth: 200 }}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              {['all','today','week','month'].map(p => (
                <button key={p} onClick={() => applyPreset(p)}
                  style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: datePreset===p ? '#3b82f6' : '#fff', color: datePreset===p ? '#fff' : '#64748b', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  {p==='all'?'All':p==='today'?'Today':p==='week'?'7 Days':'This Month'}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="date" value={dateFrom} onChange={e=>{setDateFrom(e.target.value);setDatePreset('custom');}}
                style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
              <span style={{ color: '#94a3b8' }}>to</span>
              <input type="date" value={dateTo} onChange={e=>{setDateTo(e.target.value);setDatePreset('custom');}}
                style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
            </div>
            <button onClick={downloadAllCSV}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 10, border: 'none', background: '#25D366', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              ⬇️ Export All CSV
            </button>
          </div>
        </div>
        {bid === 26 && templateNames.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '12px 24px', borderBottom: '1px solid #f1f5f9', background: '#fafbff' }}>
            <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, marginRight: 2 }}>Template:</span>
            <button onClick={() => setTemplateFilter('all')}
              style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid #e2e8f0', background: templateFilter === 'all' ? '#0f172a' : '#fff', color: templateFilter === 'all' ? '#fff' : '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              All <span style={{ opacity: 0.7 }}>{campaigns.length}</span>
            </button>
            {templateNames.map(t => {
              const count = campaigns.filter(c => c.template_name === t).length;
              return (
                <button key={t} onClick={() => setTemplateFilter(t)}
                  style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid #e2e8f0', background: templateFilter === t ? '#3b82f6' : '#fff', color: templateFilter === t ? '#fff' : '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  {t} <span style={{ opacity: 0.7 }}>{count}</span>
                </button>
              );
            })}
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {(bid === 22 ? ['Customer Name', 'Phone Number', 'Template', 'Sent', 'Delivered', 'Read', 'Failed', 'Date / Actions'] : ['Order ID', 'Customer Name', 'Order Name', 'Template', 'Sent', 'Delivered', 'Read', 'Failed', 'Date / Actions']).map(h => (
                  <th key={h} style={{ padding: '11px 16px', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #e2e8f0', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredCampaigns.length === 0 ? (
                <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 15 }}>No campaigns found</td></tr>
              ) : filteredCampaigns.map((c, i) => {
                const total = c.sent_count ?? c.total_contacts ?? 0;
                const delivered = c.delivered_count ?? 0;
                const failed = c.failed_count ?? 0;
                const read = c.read_count ?? 0;
                const { orderId, customerName, orderName } = parseName(c.campaign_name, c.customer_name, c.customer_phone);
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                    {bid !== 22 && (
                      <td style={{ padding: '13px 16px', fontWeight: 700, color: '#2563eb', fontSize: 14 }}>{orderId}</td>
                    )}
                    <td style={{ padding: '13px 16px', fontWeight: 600, color: '#0f172a', fontSize: 14 }}>{customerName}</td>
                    <td style={{ padding: '13px 16px', color: '#64748b', fontSize: 13 }}>{orderName}</td>
                    <td style={{ padding: '13px 16px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: 20, background: '#f0fdf4', color: '#16a34a', fontSize: 12, fontWeight: 700 }}>
                        {c.template_name || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: 14, color: '#374151' }}>{c.sent_count ?? 0}</td>
                    <td style={{ padding: '13px 16px', minWidth: 120 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 5, background: '#e2e8f0', borderRadius: 99 }}>
                          <div style={{ width: `${pct(delivered, total)}%`, height: '100%', background: '#10b981', borderRadius: 99 }} />
                        </div>
                        <span style={{ fontSize: 13, color: '#374151', minWidth: 55, fontWeight: 600 }}>{delivered} <span style={{ color: '#94a3b8', fontWeight: 400 }}>({pct(delivered, total)}%)</span></span>
                      </div>
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: 14, fontWeight: 600, color: read > 0 ? '#7c3aed' : '#94a3b8' }}>{read}</td>
                    <td style={{ padding: '13px 16px' }}>
                      <span onClick={() => fetchFailed(c)}
                        style={{ padding: '3px 10px', borderRadius: 20, fontSize: 13, fontWeight: 700, background: failed > 0 ? '#fef2f2' : '#f0fdf4', color: failed > 0 ? '#dc2626' : '#16a34a', cursor: 'pointer', textDecoration: failed > 0 ? 'underline' : 'none' }}>
                        {failed}
                      </span>
                    </td>
                    <td style={{ padding: '13px 16px' }}>
                      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>
                        {c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => fetchView(c)}
                          style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#2563eb', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                          👁 View
                        </button>
                        <button onClick={() => downloadCampaignCSV(c)}
                          style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#16a34a', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                          ⬇ CSV
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {allFailedModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, minWidth: 480, maxWidth: 620, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '28px 28px 16px 28px', position: 'sticky', top: 0, background: '#fff', borderRadius: '16px 16px 0 0', zIndex: 1, flexShrink: 0 }}>
              <div>
                <p style={{ fontWeight: 700, fontSize: 16, color: '#0f172a', margin: 0 }}>All Failed Messages</p>
                <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>Total: {allFailedList.length}</p>
              </div>
              <button onClick={() => setAllFailedModal(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 14, color: '#64748b' }}>Close</button>
            </div>
            <div style={{ overflowY: 'auto', padding: '0 28px 28px 28px' }}>
            {allFailedLoading ? (
              <p style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>Loading...</p>
            ) : allFailedList.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>No failed messages</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Name', 'Phone', 'Type', 'Reason', 'Date'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allFailedList.map((m, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 12px', color: '#0f172a' }}>{m.contact_name || '—'}</td>
                      <td style={{ padding: '8px 12px', color: '#374151' }}>{m.phone || '—'}</td>
                      <td style={{ padding: '8px 12px', maxWidth: 200 }}>
                        <span title={m.message || ''} style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {m.template_name || (m.message || '').split('\n')[0].slice(0, 40) || m.msg_type || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', color: '#dc2626', fontSize: 13 }}>{m.error_reason || 'Unknown'}</td>
                      <td style={{ padding: '8px 12px', color: '#94a3b8', fontSize: 12 }}>{m.created_at ? new Date(m.created_at).toLocaleDateString('en-IN') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            </div>
          </div>
        </div>
      )}

      {failedModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, minWidth: 420, maxWidth: 560, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <p style={{ fontWeight: 700, fontSize: 16, color: '#0f172a', margin: 0 }}>{failedModal.campaign_name}</p>
                <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>Failed contacts</p>
              </div>
              <button onClick={() => setFailedModal(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 14, color: '#64748b' }}>Close</button>
            </div>
            {failedLoading ? (
              <p style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>Loading...</p>
            ) : failedContacts.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>No data found</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Name</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Phone</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {failedContacts.map((fc, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 12px', color: '#0f172a' }}>{fc.contact_name || '—'}</td>
                      <td style={{ padding: '8px 12px', color: '#374151' }}>{fc.phone || '—'}</td>
                      <td style={{ padding: '8px 12px', color: '#dc2626', fontSize: 13 }}>{fc.error_reason || 'No reason'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {viewModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, minWidth: 680, maxWidth: 860, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <p style={{ fontWeight: 700, fontSize: 16, color: '#0f172a', margin: 0 }}>{viewModal.campaign_name}</p>
                <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>{viewMessages.length} messages</p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => downloadViewCSV('success')}
                  style={{ background: '#25D366', border: 'none', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 700 }}>⬇ Success CSV ({viewMessages.filter(m => m.status === 'delivered' || m.status === 'read').length})</button>
                <button onClick={() => downloadViewCSV('failed')}
                  style={{ background: '#dc2626', border: 'none', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 700 }}>⬇ Failed CSV ({viewMessages.filter(m => m.status === 'failed').length})</button>
                <button onClick={() => setViewModal(null)}
                  style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 14, color: '#64748b' }}>Close</button>
              </div>
            </div>
            {viewLoading ? (
              <p style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>Loading...</p>
            ) : viewMessages.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>No messages found</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Phone', 'Status', 'Sent At', 'Delivered At', 'Read At'].map(h => (
                      <th key={h} style={{ padding: '9px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0', fontSize: 12, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {viewMessages.map((m, i) => {
                    const ss = statusStyle(m.status);
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '9px 12px', fontWeight: 600, color: '#0f172a' }}>{m.phone}</td>
                        <td style={{ padding: '9px 12px' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: ss.bg, color: ss.color, fontSize: 12, fontWeight: 700 }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: ss.dot }} />
                            {m.status}
                          </span>
                        </td>
                        <td style={{ padding: '9px 12px', color: '#64748b', fontSize: 13 }}>{m.created_at ? new Date(m.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                        <td style={{ padding: '9px 12px', color: '#64748b', fontSize: 13 }}>{m.delivered_at ? new Date(m.delivered_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                        <td style={{ padding: '9px 12px', color: '#64748b', fontSize: 13 }}>{m.read_at ? new Date(m.read_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
